import { type FC, useRef } from "react";

import { filterByCategory } from "@/shared/constants/estateCategories";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItemActions } from "@/shared/hooks/useEstateItemActions";
import { supabase } from "@/shared/lib/supabase";
import { uploadSupportingDocument } from "@/shared/utils/uploadSupportingDocument";
import { ReviewScreenLayout } from "@/pages/postloss/modules/components/ReviewScreenLayout";
import { useReviewRedirect } from "@/pages/postloss/modules/hooks/useReviewRedirect";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import type { EstateItem } from "@/shared/types/estateTypes";

import {
  VehicleForm,
  buildVehicleName,
  parseVehicleToForm,
  validateVehicleFields,
  vehicleToItemFields,
  type VehicleFormValues,
} from "./VehicleForm";

export const VehiclesReview: FC<FlowCustomComponentProps> = ({
  onComplete,
  goToScreen,
  answers,
  setAnswer,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets, isLoading, isFetching, updateItem, isUpdatingItem, deleteItem, itemActions } =
    useEstateItemActions(estateId);
  const dialogRef = useRef<HTMLDivElement>(null);

  const items = filterByCategory(assets, "Vehicles");
  const firstName = person?.first_name ?? "their";

  const { itemsEverFound, alreadyRedirected } = useReviewRedirect({
    items,
    isLoading,
    isFetching,
    answers,
    setAnswer,
    goToScreen,
    reviewKey: "vehicles_review",
    addKey: "add_vehicle",
    fallbackScreen: "has_vehicle",
    fallbackAnswers: { ...answers, vehicles_review: "none", add_vehicle: "" },
  });

  const parseItemToForm = (item: EstateItem): VehicleFormValues =>
    parseVehicleToForm(
      item.item_name,
      item.item_type ?? "",
      item.balance,
      item.notes,
      item.metadata,
    );

  const handleSaveEdit = async (editing: {
    id: number;
    form: VehicleFormValues;
    existingFileName: string | null;
    fileRemoved: boolean;
  }) => {
    const { id, form, fileRemoved } = editing;
    if (!estateId) return;

    const validationError = Object.values(validateVehicleFields(form))[0];
    if (validationError) throw new Error(validationError);

    let sourceDocumentId: number | undefined;

    if (form.titleFile) {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return;
      sourceDocumentId = await uploadSupportingDocument(
        form.titleFile,
        estateId,
        session.access_token,
        "vehicle_title",
      );
    }

    const sourceDocUpdate = sourceDocumentId
      ? { source_document_id: sourceDocumentId }
      : fileRemoved
        ? { source_document_id: null }
        : {};

    const fields = vehicleToItemFields(form);
    await updateItem(id, {
      item_name: buildVehicleName(form),
      item_type: fields.item_type,
      balance: fields.balance,
      metadata: fields.metadata,
      ...sourceDocUpdate,
    });
  };

  return (
    <ReviewScreenLayout<VehicleFormValues>
      items={items}
      isLoading={isLoading}
      isFetching={isFetching}
      isRedirecting={items.length === 0 && !itemsEverFound && !alreadyRedirected}
      screenTitle={`Review ${firstName}'s vehicles`}
      screenSubtitle="Review the details and add any others we might have missed."
      itemLabel="vehicles"
      listTitle="Vehicles"
      editDialogTitle="Edit vehicle"
      editDialogRef={dialogRef}
      deleteDialogTitle="Delete Vehicle"
      parseItemToForm={parseItemToForm}
      renderEditForm={({ form, onChange, existingFileName, onFileRemove }) => (
        <VehicleForm
          form={form}
          onChange={onChange}
          existingTitleFileName={existingFileName}
          onTitleRemoved={onFileRemove}
        />
      )}
      onSaveEdit={handleSaveEdit}
      isUpdatingItem={isUpdatingItem}
      deleteItem={deleteItem}
      itemActions={itemActions}
      onAddAnother={() => {
        goToScreen("add_another_vehicle", {
          ...answers,
          _add_from_review: "vehicles",
        });
      }}
      onContinue={() => {
        setAnswer("add_vehicle", "done");
        setAnswer("_add_from_review", "");
        onComplete();
      }}
    />
  );
};
